import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatContext } from './ChatContext';
import { useAuth } from './AuthContext';
import { initializeFirebase } from '../../firebase';
import { collection, query, onSnapshot, addDoc, serverTimestamp, orderBy, getDocs, deleteDoc, doc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useSocket } from './SocketProvider'; // ⭐ CORRECT: Import the shared socket hook

export const ChatProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const socket = useSocket(); // ⭐ CORRECT: Get the shared socket instance from context

    const [db, setDb] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoadingConversations, setIsLoadingConversations] = useState(true);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [chatError, setChatError] = useState(null);
    const [totalUnreadCount, setTotalUnreadCount] = useState(0);

    const messagesListener = useRef(null);

    useEffect(() => {
        const loadFirebaseDb = async () => {
            try {
                const { db: firestoreDb } = await initializeFirebase();
                setDb(firestoreDb);
            } catch (error) {
                console.error("ChatProvider: Failed to initialize Firebase or get Firestore:", error);
                setChatError("Failed to initialize chat services.");
            }
        };

        if (!db) {
            loadFirebaseDb();
        }
    }, [db]);


    const fetchConversations = useCallback(async (isBackgroundRefresh = false) => {
        if (authLoading || !user) {
            setIsLoadingConversations(false);
            return [];
        }
        if (!isBackgroundRefresh) {
            setIsLoadingConversations(true);
        }
        setChatError(null);
        try {
            const endpoint = user.role === 'customer' ? '/chat/customer/conversations' : '/chat/conversations';
            const response = await api.get(endpoint);
            const fetchedConversations = response.data.conversations || [];
            
            const totalUnreads = fetchedConversations.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0);
            setTotalUnreadCount(totalUnreads);

            setConversations(fetchedConversations);
            return fetchedConversations;
        } catch (err) {
            console.error("ChatProvider: Error fetching conversations:", err);
            const errorMsg = "Failed to load conversations.";
            setChatError(errorMsg);
            if (!isBackgroundRefresh) toast.error(errorMsg);
            return [];
        } finally {
            if (!isBackgroundRefresh) {
                setIsLoadingConversations(false);
            }
        }
    }, [user]);

    // ⭐ CORRECTED: This effect now uses the shared socket from context
    useEffect(() => {
        if (user && db) {
            fetchConversations(); // Initial fetch
        } else {
            // Cleanup on logout
            setConversations([]);
            setSelectedConversation(null);
            setMessages([]);
            setTotalUnreadCount(0);
        }
    }, [user, db, fetchConversations]);

    // ⭐ CORRECTED: This effect handles socket event listeners
    useEffect(() => {
        if (!socket || !user) {
            return;
        }

        const handleConversationUpdate = (updatedConversation) => {
            console.log('Chat WebSocket: Received conversation_updated event', updatedConversation);
            fetchConversations(true); // Refresh conversation list on update
        };
        
        const handleNewMessage = (data) => {
             console.log('Chat WebSocket: Received new_message event', data);
             // Firestore's onSnapshot handles showing the new message in real-time.
             // We just need to refresh the conversation list to update unread counts and last message previews.
             fetchConversations(true);
        };

        socket.on('conversation_updated', handleConversationUpdate);
        socket.on('new_message', handleNewMessage);
        
        return () => {
            socket.off('conversation_updated', handleConversationUpdate);
            socket.off('new_message', handleNewMessage);
        };
    }, [socket, user, fetchConversations]);


    useEffect(() => {
        // This effect handles real-time messages for the *selected* conversation via Firestore.
        // It remains unchanged as it's already a real-time implementation.
        if (messagesListener.current) {
            messagesListener.current();
        }

        if (selectedConversation && user && db) {
            if (selectedConversation.isNew) {
                setMessages([]);
                setIsLoadingMessages(false);
                return;
            }

            setIsLoadingMessages(true);

            const conversationId = selectedConversation.type === 'direct'
                ? [user._id, selectedConversation.otherUser._id].sort().join('_')
                : selectedConversation.type === 'whatsapp'
                    ? `whatsapp_${selectedConversation.otherUser._id}` // shared inbox — keyed by customerId, not by which admin is looking
                    : selectedConversation.group._id;

            const messagesRef = collection(db, "chats", conversationId, "messages");
            const companyIdForQuery = (user.company && user.company._id) ? user.company._id : user.company;
            const q = query(messagesRef, where("companyId", "==", companyIdForQuery), orderBy("timestamp", "asc"));

            messagesListener.current = onSnapshot(q, (querySnapshot) => {
                const fetchedMessages = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        ...data,
                        timestamp: data.timestamp ? data.timestamp.toDate() : new Date()
                    };
                });
                setMessages(fetchedMessages);
                setIsLoadingMessages(false);
            }, (error) => {
                console.error("Error listening to messages:", error);
                setChatError("Failed to load messages in real-time.");
                setIsLoadingMessages(false);
            });
        } else {
            setMessages([]);
        }

        return () => {
            if (messagesListener.current) {
                messagesListener.current();
            }
        };
    }, [selectedConversation, user, db]);


    const selectConversation = useCallback(async (conversation) => {
        if (conversation?.isNew) {
            setSelectedConversation(conversation);
            return;
        }

        setSelectedConversation(conversation);

        if (conversation && conversation.unreadCount > 0) {
            try {
                const endpoint = user.role === 'customer' 
                    ? `/chat/customer/read-messages/direct/${conversation.otherUser._id}`
                    : conversation.type === 'direct'
                        ? `/chat/read-messages/direct/${conversation.otherUser._id}`
                        : conversation.type === 'whatsapp'
                            ? `/whatsapp/read/${conversation.otherUser._id}`
                            : `/chat/read-messages/group/${conversation.group._id}`;
                
                await api.post(endpoint);
                
                fetchConversations(true);
            } catch (err) {
                console.error("ChatProvider: Failed to mark messages as read:", err);
            }
        }
    }, [user, fetchConversations]);

    const sendMessage = useCallback(async (content) => {
        if (!selectedConversation || !user || !content.trim() || !db) return;

        // ⭐ FIX: resolve companyId up front and refuse to send if it's not
        // available yet, instead of silently writing a message doc with a
        // missing companyId (which then becomes permanently invisible, since
        // the read query filters on this field for security/isolation).
        const resolvedCompanyId = (user.company && user.company._id) ? user.company._id : user.company;
        if (!resolvedCompanyId) {
            console.error("sendMessage: user.company is not populated yet — refusing to send to avoid an orphaned message.");
            toast.error("Still loading your account — please wait a moment and try again.");
            return;
        }

        const otherUserId = selectedConversation.isNew ? selectedConversation.otherUser._id : null;

        const conversationIdForFirestore = selectedConversation.type === 'direct'
            ? [user._id, selectedConversation.otherUser._id].sort().join('_')
            : selectedConversation.group._id;

        const messagesRef = collection(db, "chats", conversationIdForFirestore, "messages");

        try {
            await addDoc(messagesRef, {
                content: content,
                senderId: user._id,
                senderName: user.contactPersonName || user.email,
                timestamp: serverTimestamp(),
                companyId: resolvedCompanyId
            });

            const endpoint = user.role === 'customer' ? '/chat/customer/update-last-message' : '/chat/update-last-message';
            const conversationIdForBackend = selectedConversation.type === 'direct' ? selectedConversation.otherUser._id : selectedConversation.group._id;
            
            await api.post(endpoint, {
                conversationId: conversationIdForBackend,
                conversationType: selectedConversation.type,
                lastMessageContent: content
            });

            if (selectedConversation.isNew && otherUserId) {
                const updatedConversations = await fetchConversations(true);
                const newConv = updatedConversations.find(c => c.type === 'direct' && c.otherUser._id === otherUserId);
                if (newConv) {
                    setSelectedConversation(newConv);
                }
            } else {
                fetchConversations(true); // Refresh conversations to show new last message
            }

        } catch (error) {
            console.error("Error sending message:", error);
            toast.error("Failed to send message.");
        }
    }, [selectedConversation, user, fetchConversations, db]);

    // ⭐ NEW: WhatsApp send — unlike sendMessage, this does NOT write to Firestore client-side.
    // The backend does that itself (after actually delivering via Twilio), then pushes the
    // real-time update, so the message only appears once it's genuinely been sent.
    const sendWhatsappMessage = useCallback(async (customerId, content) => {
        if (!content.trim()) return;
        try {
            await api.post('/whatsapp/send', { customerId, content });
            fetchConversations(true);
        } catch (error) {
            console.error("Error sending WhatsApp message:", error);
            toast.error(error.response?.data?.message || "Failed to send WhatsApp message.");
            throw error;
        }
    }, [fetchConversations]);

    // ⭐ NEW: fetches Twilio Content API templates approved for WhatsApp, used when the
    // customer hasn't messaged within the last 24h and a freeform send would be rejected.
    const fetchWhatsappTemplates = useCallback(async () => {
        try {
            const res = await api.get('/whatsapp/templates');
            return res.data;
        } catch (error) {
            console.error("Error fetching WhatsApp templates:", error);
            toast.error(error.response?.data?.message || "Failed to load WhatsApp templates.");
            throw error;
        }
    }, []);

    // ⭐ NEW: sends a WhatsApp template (business-initiated) message, same non-optimistic
    // pattern as sendWhatsappMessage — backend writes to Firestore after Twilio confirms send.
    const sendWhatsappTemplate = useCallback(async (customerId, contentSid, contentVariables, templateBody) => {
        try {
            await api.post('/whatsapp/send-template', { customerId, contentSid, contentVariables, templateBody });
            fetchConversations(true);
        } catch (error) {
            console.error("Error sending WhatsApp template:", error);
            toast.error(error.response?.data?.message || "Failed to send WhatsApp template.");
            throw error;
        }
    }, [fetchConversations]);

    const createGroup = useCallback(async (name, memberIds) => {
        try {
            const res = await api.post('/chat/groups', { name, memberIds });
            fetchConversations(true);
            return res.data;
        } catch (err) {
            throw err;
        }
    }, [fetchConversations]);

    const addMembers = useCallback(async (groupId, memberIds) => {
        try {
            await api.put(`/chat/groups/${groupId}/members`, { memberIds });
            const updatedConversations = await fetchConversations(true);
            if (selectedConversation?.group?._id === groupId) {
                const updatedConv = updatedConversations.find(c => c.group?._id === groupId);
                if (updatedConv) {
                    setSelectedConversation(updatedConv);
                }
            }
        } catch (err) {
            throw err;
        }
    }, [fetchConversations, selectedConversation]);

    const removeMembers = useCallback(async (groupId, memberIds) => {
        try {
            await api.delete(`/chat/groups/${groupId}/members`, { data: { memberIds } });
            const updatedConversations = await fetchConversations(true);
            if (selectedConversation?.group?._id === groupId) {
                const updatedConv = updatedConversations.find(c => c.group?._id === groupId);
                if (updatedConv) {
                    setSelectedConversation(updatedConv);
                }
            }
        } catch (err) {
            throw err;
        }
    }, [fetchConversations, selectedConversation]);

    const deleteGroup = useCallback(async (groupId) => {
        try {
            // 1. Delete from Backend MongoDB
            await api.delete(`/chat/groups/${groupId}`);

            // 2. Delete messages from Firebase Firestore
            if (db) {
                const messagesRef = collection(db, "chats", groupId, "messages");
                const snapshot = await getDocs(query(messagesRef));
                // Loop through and delete every message in the group
                const deletePromises = snapshot.docs.map(d => deleteDoc(doc(db, "chats", groupId, "messages", d.id)));
                await Promise.all(deletePromises);
            }

            if (selectedConversation?.group?._id === groupId) {
                setSelectedConversation(null);
            }
            fetchConversations(true);
        } catch (err) {
            console.error(`Failed to delete group: ${err.response?.data?.message || err.message}`);
            throw err;
        }
    }, [fetchConversations, selectedConversation, db]);

    const deleteConversation = useCallback(async (otherUserId) => {
        try {
            const endpoint = user.role === 'customer' 
                ? `/chat/customer/conversations/direct/${otherUserId}`
                : `/chat/conversations/direct/${otherUserId}`;

            // 1. Delete from Backend MongoDB
            await api.delete(endpoint);
            
            // 2. Delete messages from Firebase Firestore
            if (db && user) {
                // Recreate the exact room ID used for direct messages
                const conversationIdForFirestore = [user._id, otherUserId].sort().join('_');
                const messagesRef = collection(db, "chats", conversationIdForFirestore, "messages");
                const snapshot = await getDocs(query(messagesRef));
                
                // Loop through and delete every message in the direct chat
                const deletePromises = snapshot.docs.map(d => deleteDoc(doc(db, "chats", conversationIdForFirestore, "messages", d.id)));
                await Promise.all(deletePromises);
            }

            if (selectedConversation?.type === 'direct' && selectedConversation?.otherUser?._id === otherUserId) {
                setSelectedConversation(null);
            }
            fetchConversations(true);
        } catch (err) {
            console.error("ChatProvider: Failed to delete conversation:", err);
            throw err;
        }
    }, [fetchConversations, selectedConversation, user, db]);

    const value = {
        isConnected: socket?.connected || false,
        onlineUsers: [],
        conversations,
        selectedConversation,
        messages,
        isLoadingConversations,
        isLoadingMessages,
        chatError,
        totalUnreadCount,
        selectConversation,
        sendMessage,
        sendWhatsappMessage,
        fetchWhatsappTemplates,
        sendWhatsappTemplate,
        createGroup,
        addMembers,
        removeMembers,
        deleteGroup,
        deleteConversation,
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};